import { Button, Typography, Select, StackView, FlexGrid, ExpandCollapse, Tags } from '@telus-uds/components-web';
import PropTypes from 'prop-types';
import { isBefore, format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';

import { useLocaleContext } from '../context/LocaleContext';
import EN from '../../EN.json';
import FR from '../../FR.json';
import { InputWrapper, DateWrapper } from './css';
import { FILTERS_INIT_STATE } from './constants';
import { Box, Text } from '../design/css';

const Filters = ({
  onApplyFilters,
  onResetFilters,
  role,
  assignedUsers,
  defaultFilters
}) => {
  const local = useLocaleContext();
  const {
    ORDERS: { filterByReportedDate },
    DEFECT_MANAGEMENT: {
      FILTER = '',
      FILTER_BY_WEBFORM = {},
      FILTER_BY_CATEGORY = {},
      FILTER_BY_STATUS = {},
      FILTER_BY_ASSIGNEE = {},
      FILTER_BY_DEFECT_CATEGORY_OPTIONS,
      APPLY = '',
      RESET = '',
      STATUS_OPTIONS,
      WEBFORM_OPTIONS,
      FROM,
      TO,
      Showing_Result_For,
      filterKeyToShortTitleMap
    } = {}
  } = local === 'en-US' ? EN : FR;

  const [filter, setFilter] = useState({
    ...FILTERS_INIT_STATE,
    ...defaultFilters
  });

  useEffect(() => {
    setFilter({ ...filter, ...defaultFilters });
  }, [defaultFilters]);

  const onRemoveTagFilter = key => {
    const tempFilters = filter;
    delete tempFilters[key];
    setFilter(tempFilters);
    onApplyFilters(tempFilters);
  };

  // Convert defaultFilters to Tags items format
  const tagItems = Object.keys(defaultFilters)
    .filter(key => defaultFilters[key])
    .map(key => ({
      id: key,
      label: `${filterKeyToShortTitleMap[key]}: ${
        ['startDate', 'endDate'].includes(key)
          ? new Date(defaultFilters[key]).toDateString()
          : defaultFilters[key]
      }`
    }));

  // Get current selected values (all active filter keys)
  const selectedValues = Object.keys(defaultFilters).filter(key => defaultFilters[key]);

  // Handle tag removal when a tag is deselected
  const handleTagsChange = (selectedIds) => {
    // Find which tag was removed (was in selectedValues but not in selectedIds)
    const removedId = selectedValues.find(id => !selectedIds.includes(id));
    if (removedId) {
      onRemoveTagFilter(removedId);
    }
  };

  return (
    <StackView space={3}>
      <ExpandCollapse>
        {(expandProps) => (
          <ExpandCollapse.Panel
            {...expandProps}
            panelId={FILTER}
            control={<Typography variant={{ size: 'h2', bold: true }}>{FILTER}</Typography>}
          >
            <StackView space={1}>
              <FlexGrid>
                <FlexGrid.Row Style="margin-bottom:10px">
                  <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                    <Select
                      label={FILTER_BY_WEBFORM.title}
                      placeholder={FILTER_BY_WEBFORM.placeholder}
                      value={filter.defectWebform || ''}
                      onChange={(value) =>
                        setFilter({ ...filter, defectWebform: value })
                      }
                    >
                      {WEBFORM_OPTIONS.map(o => (
                        <Select.Item key={o} value={o}>
                          {o}
                        </Select.Item>
                      ))}
                    </Select>
                  </FlexGrid.Col>
                  <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                    <Select
                      label={FILTER_BY_CATEGORY.title}
                      placeholder={FILTER_BY_CATEGORY.placeholder}
                      value={filter.defectCategory || ''}
                      onChange={(value) =>
                        setFilter({
                          ...filter,
                          defectCategory: value
                        })
                      }
                    >
                      {FILTER_BY_DEFECT_CATEGORY_OPTIONS.map(o => (
                        <Select.Item key={o} value={o}>
                          {o}
                        </Select.Item>
                      ))}
                    </Select>
                  </FlexGrid.Col>
                  <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                    <Select
                      label={FILTER_BY_STATUS.title}
                      placeholder={FILTER_BY_STATUS.placeholder}
                      value={filter.status || ''}
                      onChange={(value) =>
                        setFilter({ ...filter, status: value })
                      }
                    >
                      {STATUS_OPTIONS.map(o => (
                        <Select.Item key={o} value={o}>
                          {o}
                        </Select.Item>
                      ))}
                    </Select>
                  </FlexGrid.Col>
                  {role.name === 'ADMIN' ? (
                    <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                      <Select
                        label={FILTER_BY_ASSIGNEE.title}
                        placeholder={FILTER_BY_ASSIGNEE.placeholder}
                        value={filter.assignee || ''}
                        onChange={(value) =>
                          setFilter({ ...filter, assignee: value })
                        }
                      >
                        {assignedUsers.map(o => (
                          <Select.Item key={o.email} value={o.email}>
                            {`${o.firstName} ${o.lastName}`}
                          </Select.Item>
                        ))}
                      </Select>
                    </FlexGrid.Col>
                  ) : (
                    ''
                  )}
                  <FlexGrid.Col xs={12}>
                    <Box top={10}>
                      <Typography variant={{ bold: true }}>{filterByReportedDate} </Typography>
                    </Box>
                  </FlexGrid.Col>
                  <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                    <InputWrapper>
                      <DateWrapper>
                        <DatePicker
                          portalId="datepicker-portal"
                          dateFormat="yyyy/MM/dd"
                          placeholderText={FROM}
                          selected={
                            filter.startDate ? new Date(filter.startDate) : ''
                          }
                          onChange={value => {
                            const isEndDateBeforeStartDate =
                              filter.endDate &&
                              isBefore(new Date(filter.endDate), value);
                            setFilter({
                              ...filter,
                              startDate: format(value, 'yyyy-MM-dd'),
                              ...(isEndDateBeforeStartDate && { endDate: '' })
                            });
                          }}
                        />
                      </DateWrapper>
                    </InputWrapper>
                  </FlexGrid.Col>
                  <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                    <InputWrapper>
                      <DateWrapper>
                        <DatePicker
                          portalId="datepicker-portal"
                          dateFormat="yyyy/MM/dd"
                          placeholderText={TO}
                          minDate={
                            filter.startDate ? new Date(filter.startDate) : ''
                          }
                          selected={
                            filter.endDate ? new Date(filter.endDate) : ''
                          }
                          onChange={value => {
                            setFilter({
                              ...filter,
                              endDate: format(value, 'yyyy-MM-dd')
                            });
                          }}
                        />
                      </DateWrapper>
                    </InputWrapper>
                  </FlexGrid.Col>
                </FlexGrid.Row>
                <Box
                  display="flex"
                  justifyContent="center"
                  flexDirection="row"
                  top={8}
                  mdJustifyContent="flex-start"
                  mdWidth={100}
                  mdPadding="16px 0"
                  margin="0 auto"
                >
                  <Button
                    variant={{ priority: 'low' }}
                    onClick={() =>
                      onApplyFilters({
                        ...filter
                        // ...(filter.startDate
                        //   ? {
                        //     startDate: filter.startDate
                        //       .toISOString()
                        //       .split('T')[0]
                        //   }
                        //   : {}),
                        // ...(filter.endDate
                        //   ? {
                        //     endDate: filter.endDate.toISOString().split('T')[0]
                        //   }
                        //   : {})
                      })
                    }
                  >
                    {APPLY}
                  </Button>
                  <Box width="2"> </Box>
                  <Button
                    onClick={() => {
                      setFilter({ ...FILTERS_INIT_STATE });
                      onResetFilters(true);
                    }}
                  >
                    {RESET}
                  </Button>
                </Box>
              </FlexGrid>
            </StackView>
          </ExpandCollapse.Panel>
        )}
      </ExpandCollapse>
      {!!Object.values(defaultFilters).filter(value => !!value).length && (
        <>
          <Typography heading="h3" variant={{ size: 'h3', bold: true }} block>
            {Showing_Result_For}
          </Typography>
          <Tags
            items={tagItems}
            values={selectedValues}
            onChange={handleTagsChange}
          />
        </>
      )}
    </StackView>
  );
};

Filters.propTypes = {
  onApplyFilters: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  role: PropTypes.shape({ name: PropTypes.string }).isRequired,
  assignedUsers: PropTypes.shape().isRequired,
  defaultFilters: PropTypes.shape().isRequired
};
export default Filters;
